import React, { useState } from 'react';
import { Form, Input, Button, message, Card, Typography, Space } from 'antd';
import { SendOutlined, MailOutlined, UserOutlined, MessageOutlined, FileTextOutlined } from '@ant-design/icons';
import apiService from '@/services/api.service';
import { API_ENDPOINTS } from '@/constants/api.constants';
import { logger } from '@/utils/logger';
import type { ApiResponse } from '@/types/api';
import { useTranslation } from 'react-i18next';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;

interface FeedbackFormData {
  name?: string;
  email: string;
  subject?: string;
  message: string;
}

const ContactForm: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation(['common', 'feedback']);

  const onFinish = async (values: FeedbackFormData) => {
    try {
      setLoading(true);
      
      const response = await apiService.post<ApiResponse<{ message: string }>>(
        API_ENDPOINTS.FEEDBACK.SUBMIT,
        {
          name: values.name?.trim() || undefined,
          email: values.email.trim(),
          subject: values.subject?.trim() || undefined,
          message: values.message.trim()
        }
      );

      // Проверяем структуру ответа
      const responseData = response.data as any;
      if (responseData && (responseData.status === 'success' || responseData.success === true)) {
        const messageText = responseData.data?.message || responseData.message || t('feedback:messages.successDetails');
        message.success(messageText);
        form.resetFields();
      } else {
        const errorText = responseData?.message || t('feedback:messages.error');
        message.error(errorText);
      }
    } catch (error: any) {
      logger.error('Error submitting feedback form:', error);
      
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.errors?.[0]?.msg || 
                          t('feedback:messages.errorDetails');
      
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '24px' }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Title level={2}>
              <MailOutlined style={{ marginRight: 8 }} />
              {t('feedback:title')}
            </Title>
            <Paragraph>
              {t('feedback:description')}
            </Paragraph>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            autoComplete="off"
          >
            <Form.Item
              name="name"
              label={
                <span>
                  <UserOutlined style={{ marginRight: 8 }} />
                  {t('feedback:fields.name.label')}
                </span>
              }
              rules={[
                { max: 100, message: t('feedback:validation.nameMaxLength') }
              ]}
            >
              <Input 
                placeholder={t('feedback:fields.name.placeholder')} 
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="email"
              label={
                <span>
                  <MailOutlined style={{ marginRight: 8 }} />
                  {t('feedback:fields.email.label')}
                </span>
              }
              rules={[
                { required: true, message: t('feedback:fields.email.required') },
                { type: 'email', message: t('feedback:fields.email.invalid') }
              ]}
            >
              <Input 
                type="email"
                placeholder={t('feedback:fields.email.placeholder')} 
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="subject"
              label={
                <span>
                  <FileTextOutlined style={{ marginRight: 8 }} />
                  {t('feedback:fields.subject.label')}
                </span>
              }
              rules={[
                { max: 200, message: t('feedback:validation.subjectMaxLength') }
              ]}
            >
              <Input 
                placeholder={t('feedback:fields.subject.placeholder')} 
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="message"
              label={
                <span>
                  <MessageOutlined style={{ marginRight: 8 }} />
                  {t('feedback:fields.message.label')}
                </span>
              }
              rules={[
                { required: true, message: t('feedback:fields.message.required') },
                { min: 10, message: t('feedback:fields.message.minLength') },
                { max: 5000, message: t('feedback:fields.message.maxLength') }
              ]}
            >
              <TextArea
                rows={6}
                placeholder={t('feedback:fields.message.placeholder')}
                showCount
                maxLength={5000}
                size="large"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SendOutlined />}
                loading={loading}
                size="large"
                block
              >
                {t('feedback:buttons.submit')}
              </Button>
            </Form.Item>
          </Form>
        </Space>
      </Card>
    </div>
  );
};

export default ContactForm;
